import { useState, useRef, useEffect, type ReactNode, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarComponent from "./SidebarComponent";
import type { UserProfile } from "./Sidebar";
import { getRoleLabel } from "@/hooks/useRole";
import { notificationService } from "@/services/api";

interface DashboardLayoutProps {
  children: ReactNode;
  user: UserProfile;
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
}

function getQuickCreateOptions(rol: string) {
  switch (rol) {
    case "director":
    case "sostenedor":
      return [
        { label: "Descargar Reporte Consolidado", icon: "ri-file-chart-line", path: "/director/reportes/consolidado", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Ver Desempeño", icon: "ri-team-line", path: "/director/reportes/desempeno", color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Ver Judicializados", icon: "ri-scales-3-line", path: "/orientacion/judicializados", color: "text-red-600", bg: "bg-red-50" },
        { label: "Nuevo Req. Judicial", icon: "ri-file-warning-line", path: "/judicial/nuevo", color: "text-purple-600", bg: "bg-purple-50" },
      ];
    case "convivencia":
      return [
        { label: "Nuevo Caso Convivencia", icon: "ri-add-circle-line", path: "/convivencia/casos/nuevo", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Registrar Entrevista", icon: "ri-mic-line", path: "/convivencia/entrevistas", color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Registrar Medida", icon: "ri-hand-heart-line", path: "/convivencia/medidas", color: "text-amber-600", bg: "bg-amber-50" },
        { label: "Nueva Derivación", icon: "ri-share-forward-line", path: "/convivencia/derivaciones", color: "text-accent-600", bg: "bg-accent-50" },
      ];
    case "orientador":
      return [
        { label: "Solicitar Insumo Docente", icon: "ri-send-plane-line", path: "/orientacion/insumos", color: "text-amber-600", bg: "bg-amber-50" },
        { label: "Generar Informe IA", icon: "ri-magic-line", path: "/ia/informe", color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Ver Judicializados", icon: "ri-scales-3-line", path: "/orientacion/judicializados", color: "text-red-600", bg: "bg-red-50" },
        { label: "Nuevo Req. Judicial", icon: "ri-file-warning-line", path: "/judicial/nuevo", color: "text-purple-600", bg: "bg-purple-50" },
      ];
    case "psicologo":
      return [
        { label: "Registrar Intervención", icon: "ri-heart-pulse-line", path: "/psicologo/intervenciones", color: "text-accent-600", bg: "bg-accent-50" },
        { label: "Derivación Interna", icon: "ri-arrow-left-right-line", path: "/psicologo/derivaciones-internas", color: "text-amber-600", bg: "bg-amber-50" },
        { label: "Derivación Externa", icon: "ri-external-link-line", path: "/psicologo/derivaciones-externas", color: "text-red-600", bg: "bg-red-50" },
        { label: "Generar Informe", icon: "ri-file-text-line", path: "/psicologo/informes", color: "text-emerald-600", bg: "bg-emerald-50" },
      ];
    case "inspector":
      return [
        { label: "Registrar Incidente", icon: "ri-add-circle-line", path: "/convivencia/casos/nuevo", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Consultar Protocolo", icon: "ri-book-open-line", path: "/ia/protocolos", color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Derivar a Convivencia", icon: "ri-share-forward-line", path: "/convivencia/derivaciones", color: "text-accent-600", bg: "bg-accent-50" },
      ];
    case "profesor_jefe":
      return [
        { label: "Completar Formulario", icon: "ri-file-text-line", path: "/profesor-jefe/solicitudes", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Ver Mis Estudiantes", icon: "ri-team-line", path: "/estudiantes", color: "text-emerald-600", bg: "bg-emerald-50" },
      ];
    case "docente":
      return [
        { label: "Completar Insumo", icon: "ri-file-text-line", path: "/docente/solicitudes", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Consultar Protocolo", icon: "ri-chat-3-line", path: "/ia/consulta", color: "text-emerald-600", bg: "bg-emerald-50" },
      ];
    case "admin":
      return [
        { label: "Gestionar Usuarios", icon: "ri-team-line", path: "/admin/usuarios", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Ver Auditoría", icon: "ri-shield-check-line", path: "/admin/auditoria", color: "text-foreground-600", bg: "bg-foreground-50" },
      ];
    default:
      return [
        { label: "Nuevo Caso Convivencia", icon: "ri-add-circle-line", path: "/convivencia/casos/nuevo", color: "text-primary-600", bg: "bg-primary-50" },
        { label: "Consultar Protocolo", icon: "ri-chat-3-line", path: "/ia/consulta", color: "text-accent-600", bg: "bg-accent-50" },
      ];
  }
}

export default function DashboardLayout({ children, user, title, subtitle, actions }: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [navBarActive, setNavBarActive] = useState(false);
  const [bellRinging, setBellRinging] = useState(false);
  const [pageKey, setPageKey] = useState(0);
  
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    try {
      const list = await notificationService.list();
      setNotifications(list);
      const countRes = await notificationService.countUnread();
      setUnreadCount(countRes.count);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [showNotifications]);

  const handleMarkAsRead = async (id: string, urlDestino?: string) => {
    try {
      await notificationService.markAsRead(id);
      fetchNotifications();
      if (urlDestino) {
        navigate(urlDestino);
        setShowNotifications(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const quickCreateRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  const quickCreateOptions = useMemo(() => getQuickCreateOptions(user.rol), [user.rol]);

  // ── Route change → trigger progress bar + re-animate page ──
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      setNavBarActive(true);
      setPageKey((k) => k + 1);
      const t = setTimeout(() => setNavBarActive(false), 750);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  // Bell ring every 45s to draw attention to notifications
  useEffect(() => {
    const interval = setInterval(() => {
      setBellRinging(true);
      setTimeout(() => setBellRinging(false), 1100);
    }, 45000);
    // Ring once on mount after 3s
    const initial = setTimeout(() => {
      setBellRinging(true);
      setTimeout(() => setBellRinging(false), 1100);
    }, 3000);
    return () => { clearInterval(interval); clearTimeout(initial); };
  }, []);

  // Close popups on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (quickCreateRef.current && !quickCreateRef.current.contains(e.target as Node)) {
        setShowQuickCreate(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [showSearch]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setShowSearch(true); }
      if (e.key === "Escape") { setShowSearch(false); setShowQuickCreate(false); }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim().toLowerCase();
    if (!q) return;
    if (q.includes("convivencia") || q.includes("caso")) navigate("/convivencia/casos");
    else if (q.includes("estudiante") || q.includes("alumno")) navigate("/estudiantes");
    else if (q.includes("informe") || q.includes("tribunal")) navigate("/orientacion/informes");
    else if (q.includes("protocolo") || q.includes("normativa")) navigate("/ia/consulta");
    else if (q.includes("insumo") || q.includes("docente")) navigate("/orientacion/insumos");
    else if (q.includes("orientacion")) navigate("/orientacion");
    else if (q.includes("psicolog")) navigate("/psicologo");
    else navigate("/resumen");
    setShowSearch(false);
    setSearchQuery("");
  };

  return (
    <div className="flex h-screen bg-background-50 overflow-hidden">

      {/* ── Top nav progress bar ── */}
      {navBarActive && (
        <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] overflow-hidden pointer-events-none">
          <div
            className="h-full animate-nav-bar rounded-full"
            style={{ background: "linear-gradient(90deg, oklch(var(--primary-500)), oklch(var(--accent-500)), oklch(var(--secondary-500)))" }}
          />
        </div>
      )}

      <SidebarComponent
        user={user}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto relative">

        {/* ── Ambient floating orbs in content background ── */}
        <div className="pointer-events-none fixed inset-0 overflow-hidden z-0" style={{ left: sidebarCollapsed ? 68 : 256 }}>
          <div
            className="absolute w-[500px] h-[500px] rounded-full blur-[120px] animate-orb-1"
            style={{ top: "-10%", right: "-5%", background: "oklch(var(--primary-400) / 0.055)" }}
          />
          <div
            className="absolute w-[400px] h-[400px] rounded-full blur-[100px] animate-orb-2"
            style={{ bottom: "0%", left: "-5%", background: "oklch(var(--accent-400) / 0.045)" }}
          />
          <div
            className="absolute w-[300px] h-[300px] rounded-full blur-[80px] animate-orb-3"
            style={{ top: "40%", left: "45%", background: "oklch(var(--secondary-400) / 0.035)" }}
          />
        </div>

        {/* ── Top header ── */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-background-200/50 px-4 md:px-6 py-3 animate-header-enter">
          <div className="flex items-center gap-4">
            {/* Search */}
            <button
              onClick={() => setShowSearch(true)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 bg-background-50/80 border border-background-200/70 rounded-xl text-sm text-foreground-400
                hover:border-primary-200/60 hover:bg-white hover:text-foreground-600
                transition-all duration-200 cursor-pointer min-w-[280px] shadow-inner-soft group"
            >
              <span className="flex items-center justify-center w-4 h-4 group-hover:scale-110 transition-transform duration-200">
                <i className="ri-search-line text-foreground-400 group-hover:text-primary-400 transition-colors" />
              </span>
              <span className="flex-1 text-left text-sm">Buscar caso, estudiante, informe...</span>
              <span className="text-[11px] text-foreground-300 bg-background-200/70 px-1.5 py-0.5 rounded-md font-mono font-medium">⌘K</span>
            </button>

            <button
              onClick={() => setShowSearch(true)}
              className="sm:hidden flex items-center justify-center w-10 h-10 bg-background-50/80 border border-background-200/70 rounded-xl hover:bg-white cursor-pointer shadow-inner-soft hover:scale-105 transition-all duration-200"
            >
              <i className="ri-search-line text-foreground-500" />
            </button>

            <div className="flex-1 min-w-0">
              {title && (
                <h1 className="text-lg md:text-xl font-bold text-foreground-900 truncate hidden sm:block tracking-tight animate-fade-in">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-sm text-foreground-500 mt-0.5 truncate hidden md:block animate-fade-in" style={{ animationDelay: "80ms" }}>
                  {subtitle}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Notification bell — rings periodically */}
              <div className="relative" ref={notificationsRef}>
                <button
                  className="relative flex items-center justify-center w-10 h-10 rounded-xl hover:bg-background-100/80 transition-all duration-200 cursor-pointer hover:scale-105 press-effect"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <span className={`flex items-center justify-center w-5 h-5 ${bellRinging && unreadCount > 0 ? "animate-bell-ring" : ""}`}>
                    <i className="ri-notification-3-line text-foreground-500 text-lg" />
                  </span>
                  {/* Pulsing red dot (only if unreadCount > 0) */}
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex">
                      <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-primary-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500 border-2 border-white" />
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 bg-white/95 backdrop-blur-xl border border-background-200/60 rounded-2xl p-2 min-w-[320px] shadow-soft-lg animate-scale-in z-50">
                    <div className="flex justify-between items-center px-3 py-2 border-b border-background-100 mb-1">
                      <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider flex items-center gap-1.5">
                        <i className="ri-notification-line text-primary-400" />
                        Notificaciones ({unreadCount})
                      </p>
                      {unreadCount > 0 && (
                        <button 
                          onClick={handleMarkAllAsRead} 
                          className="text-[10px] text-primary-500 hover:text-primary-600 font-semibold cursor-pointer border-none bg-transparent"
                        >
                          Marcar todas leídas
                        </button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {notifications.map((notif) => {
                        const typeColors: Record<string, string> = {
                          alerta: "bg-red-500",
                          recordatorio: "bg-amber-500",
                          informacion: "bg-blue-500"
                        };
                        return (
                          <div 
                            key={notif.id}
                            onClick={() => handleMarkAsRead(notif.id, notif.url_destino || undefined)} 
                            className={`p-2.5 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-background-100 text-left ${
                              notif.leida ? "opacity-60 hover:bg-background-50/50" : "bg-primary-50/20 hover:bg-primary-50/50"
                            }`}
                          >
                            <p className="text-xs font-semibold text-foreground-900 flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${typeColors[notif.tipo] || "bg-foreground-400"} flex-shrink-0`} />
                              <span className="truncate">{notif.titulo}</span>
                            </p>
                            <p className="text-[10px] text-foreground-500 mt-0.5 leading-relaxed">{notif.mensaje}</p>
                          </div>
                        );
                      })}
                      {notifications.length === 0 && (
                        <p className="text-xs text-foreground-400 text-center py-8">No tienes notificaciones pendientes.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {actions}

              {/* Role indicator with live dot */}
              <div className="hidden sm:flex items-center gap-2 text-sm text-foreground-600 bg-background-50/80 border border-background-200/50 rounded-lg px-2.5 py-1.5 hover:border-background-300/60 transition-colors">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-breathe" />
                <span className="text-xs font-medium">{getRoleLabel(user.rol)}</span>
              </div>

              {/* Avatar */}
              <div className="relative" ref={profileMenuRef}>
                <div
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0 cursor-pointer
                    ring-2 ring-primary-100/60 hover:ring-primary-400/50 transition-all duration-200 hover:scale-105 press-effect"
                >
                  <span className="text-sm font-bold text-white">{user.nombre.charAt(0).toUpperCase()}</span>
                </div>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 bg-white/95 backdrop-blur-xl border border-background-200/60 rounded-2xl p-2 min-w-[200px] shadow-soft-lg animate-scale-in z-50">
                    <div className="px-3 py-2 border-b border-background-100 mb-1">
                      <p className="text-xs font-bold text-foreground-900 truncate">{user.nombre}</p>
                      <p className="text-[10px] text-foreground-400 truncate">{user.email || "director@colegio.cl"}</p>
                    </div>
                    <button
                      onClick={() => { navigate("/admin/configuracion"); setShowProfileMenu(false); }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-settings-4-line text-sm text-foreground-400" />
                      Mi Configuración
                    </button>
                    <button
                      onClick={() => { navigate("/director"); setShowProfileMenu(false); }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-dashboard-line text-sm text-foreground-400" />
                      Panel Director
                    </button>
                    <div className="border-t border-background-100 my-1" />
                    <button
                      onClick={() => {
                        localStorage.removeItem("convivencia_user");
                        navigate("/login");
                        setShowProfileMenu(false);
                      }}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-logout-box-line text-sm text-red-500" />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* ── Page content — re-animates on every route change ── */}
        <div
          key={pageKey}
          className="flex-1 p-4 md:p-6 relative z-10 animate-page-enter"
        >
          {children}
        </div>
      </main>

      {/* ── Floating Quick Create Button ── */}
      <div ref={quickCreateRef} className="fixed bottom-6 right-6 z-40">
        {showQuickCreate && (
          <div className="absolute bottom-[72px] right-0 bg-white/95 backdrop-blur-xl border border-background-200/60 rounded-2xl p-2 min-w-[250px] animate-scale-in">
            <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
              <i className="ri-add-circle-line text-primary-400" />
              Acción rápida
            </p>
            {quickCreateOptions.map((option, idx) => (
              <button
                key={option.path}
                onClick={() => { navigate(option.path); setShowQuickCreate(false); }}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-foreground-700
                  hover:bg-background-50 transition-all duration-150 cursor-pointer whitespace-nowrap hover:translate-x-1
                  animate-sidebar-item`}
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <span className={`flex items-center justify-center w-8 h-8 rounded-xl ${option.bg} ${option.color} flex-shrink-0`}>
                  <i className={`${option.icon} text-base`} />
                </span>
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* FAB button — pulses when idle */}
        <button
          onClick={() => setShowQuickCreate(!showQuickCreate)}
          className={`flex items-center justify-center w-14 h-14 rounded-2xl transition-all duration-300 ease-out-expo cursor-pointer press-effect
            ${showQuickCreate
              ? "bg-foreground-800 text-white rotate-45"
              : "bg-primary-500 text-white hover:bg-primary-600 hover:scale-110 animate-fab-pulse"
            }`}
        >
          <i className="ri-add-line text-2xl" />
        </button>
      </div>

      {/* ── Global Search ── */}
      {showSearch && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-black/20 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowSearch(false)}
        >
          <div
            className="bg-white/95 backdrop-blur-2xl rounded-2xl w-full max-w-xl mx-4 overflow-hidden animate-scale-in border border-background-200/60"
            style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSearch}>
              <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200/50">
                <span className="flex items-center justify-center w-5 h-5 text-primary-400">
                  <i className="ri-search-line text-lg" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar estudiante, caso, informe o protocolo..."
                  className="flex-1 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400 font-medium"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery("")} className="text-foreground-300 hover:text-foreground-600 transition-colors cursor-pointer">
                    <i className="ri-close-circle-line" />
                  </button>
                )}
                <span className="text-[11px] text-foreground-300 bg-background-100 px-2 py-1 rounded-md font-mono font-medium">ESC</span>
              </div>
            </form>
            <div className="px-4 py-3">
              <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-3 px-1">Accesos rápidos</p>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { label: "Casos activos",     icon: "ri-folder-3-line",    path: "/convivencia/casos",    bg: "hover:bg-primary-50",   color: "text-primary-500"   },
                  { label: "Estudiantes",        icon: "ri-user-search-line", path: "/estudiantes",          bg: "hover:bg-accent-50",    color: "text-accent-500"    },
                  { label: "Consultar protocolo",icon: "ri-chat-3-line",      path: "/ia/consulta",          bg: "hover:bg-emerald-50",   color: "text-emerald-500"   },
                  { label: "Informes tribunal",  icon: "ri-scales-line",      path: "/orientacion/informes", bg: "hover:bg-secondary-50", color: "text-secondary-500" },
                ].map((item, idx) => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setShowSearch(false); }}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground-700 ${item.bg}
                      transition-all duration-150 cursor-pointer font-medium hover:translate-x-0.5 animate-sidebar-item`}
                    style={{ animationDelay: `${idx * 40}ms` }}
                  >
                    <span className={`flex items-center justify-center w-4 h-4 ${item.color}`}>
                      <i className={item.icon} />
                    </span>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}